import { useParams, Link } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import { Toaster } from "sonner";
import { ContentProvider, useContent } from "@/context/ContentContext";
import { Analytics } from "@/components/site/Analytics";
import { Grain } from "@/components/site/Grain";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";

const embed = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vim = url.match(/vimeo\.com\/(\d+)/);
  if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
  return null;
};

const Block = ({ block }) => {
  if (block.type === "image") {
    if (!block.image) return null;
    return (
      <Reveal className="my-4">
        <img src={block.image} alt={block.caption || ""} className="w-full rounded-lg" />
        {block.caption && <p className="mt-3 text-sm text-secondary2 text-center">{block.caption}</p>}
      </Reveal>
    );
  }
  if (block.type === "video") {
    const src = embed(block.url);
    return (
      <Reveal className="my-4">
        {block.title && <h3 className="font-serif text-3xl text-white mb-4">{block.title}</h3>}
        <div className="relative w-full aspect-video rounded-lg overflow-hidden border border-white/10 bg-black">
          {src ? <iframe src={src} title={block.title || "video"} className="w-full h-full" allow="autoplay; fullscreen" allowFullScreen />
            : <video src={block.url} controls className="w-full h-full object-cover" />}
        </div>
      </Reveal>
    );
  }
  return (
    <Reveal className="my-4">
      {block.heading && <h2 className="font-serif text-4xl md:text-5xl text-white mb-5 tracking-tight">{block.heading}</h2>}
      {block.body && <p className="text-base md:text-lg text-secondary2 font-light leading-relaxed whitespace-pre-line">{block.body}</p>}
    </Reveal>
  );
};

const PageBody = () => {
  const { content } = useContent();
  const { slug } = useParams();
  const page = (content.pages || []).find((p) => p.slug === slug && p.published);

  return (
    <div className="App bg-[#050505] min-h-screen flex flex-col">
      <Grain />
      <Toaster theme="dark" position="bottom-right" />
      <Navbar />
      <main className="flex-1 pt-32 md:pt-40 pb-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {!page ? (
            <div className="py-24 text-center">
              <div className="text-[11px] tracking-[0.35em] uppercase text-electric mb-4">Erreur 404</div>
              <h1 className="font-serif text-5xl md:text-7xl text-white mb-8">Page introuvable</h1>
              <Link to="/" className="inline-flex items-center px-8 py-4 border border-white/20 text-white rounded-full text-[13px] tracking-[0.12em] uppercase hover:border-electric hover:text-electric transition-colors">Retour à l'accueil</Link>
            </div>
          ) : (
            <>
              <Reveal>
                <div className="flex items-center gap-3 mb-6">
                  <span className="h-px w-10 bg-electric" />
                  <span className="text-[11px] tracking-[0.35em] uppercase text-electric">Karams Studio</span>
                </div>
                <h1 className="font-serif text-5xl md:text-7xl text-white tracking-tight leading-[0.95] mb-14">{page.title}</h1>
              </Reveal>
              <div className="space-y-2">
                {(page.blocks || []).map((b, i) => <Block key={i} block={b} />)}
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default function DynamicPage() {
  return (
    <ContentProvider>
      <ReactLenis root options={{ lerp: 0.08, duration: 1.2, smoothWheel: true }}>
        <Analytics />
        <PageBody />
      </ReactLenis>
    </ContentProvider>
  );
}
